import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Button,
  message,
  Popconfirm,
  Tag,
  Space,
  Spin,
} from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { BASE_API_URL } from "../../../utils/consts";
import AddExtraIncomeModal from "../AddExtraIncomeModal/AddExtraIncomeModal";

interface ManageExtraIncomeModalProps {
  visible: boolean;
  onClose: () => void;
  dateRange: [Dayjs | null, Dayjs | null];
  locationId?: string;
  locationFilters?: string[];
  onUpdate: () => void;
}

interface ExtraIncomeEntry {
  _id: string;
  date: string;
  amount: number;
  description: string;
  typeId: { _id: string; name: string; color: string };
  locationId: { _id: string; name: string };
}

const ManageExtraIncomeModal: React.FC<ManageExtraIncomeModalProps> = ({
  visible,
  onClose,
  dateRange,
  locationId,
  locationFilters = [],
  onUpdate,
}) => {
  const userData = useSelector((state: any) => state.userData.user);
  const [entries, setEntries] = useState<ExtraIncomeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<ExtraIncomeEntry | null>(
    null
  );

  const fetchEntries = async () => {
    if (!userData?.clientId || !dateRange[0] || !dateRange[1]) return;

    setLoading(true);
    try {
      const params: any = {
        clientId: userData.clientId,
        startDate: dateRange[0].format("YYYY-MM-DD"),
        endDate: dateRange[1].format("YYYY-MM-DD"),
      };

      // Use locationFilters if provided, otherwise fall back to single locationId
      if (locationFilters.length > 0) {
        params.locationIds = locationFilters.join(",");
      } else if (locationId) {
        params.locationId = locationId;
      }

      const response = await axios.get(`${BASE_API_URL}/extra-income`, {
        params,
      });

      setEntries(response.data.entries || []);
    } catch (error) {
      console.error("Error fetching extra income entries:", error);
      message.error("Greška pri učitavanju dodatnih prihoda");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visible,
    dateRange?.[0]?.format("YYYY-MM-DD"),
    dateRange?.[1]?.format("YYYY-MM-DD"),
    locationId,
    locationFilters?.join(","),
  ]);

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${BASE_API_URL}/extra-income/${id}`);
      message.success("Dodatni prihod je obrisan");
      fetchEntries();
      onUpdate();
    } catch (error) {
      console.error("Error deleting extra income:", error);
      message.error("Greška pri brisanju dodatnog prihoda");
    }
  };

  const handleEdit = (entry: ExtraIncomeEntry) => {
    setEditingEntry(entry);
    setIsAddModalOpen(true);
  };

  const handleModalClose = () => {
    setEditingEntry(null);
    setIsAddModalOpen(false);
  };

  const handleSuccess = () => {
    fetchEntries();
    onUpdate();
    handleModalClose();
  };

  const columns = [
    {
      title: "Datum",
      dataIndex: "date",
      key: "date",
      render: (date: string) => dayjs(date).format("DD.MM.YYYY"),
      sorter: (a: ExtraIncomeEntry, b: ExtraIncomeEntry) =>
        dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: "Tip",
      dataIndex: ["typeId", "name"],
      key: "type",
      render: (_: any, record: ExtraIncomeEntry) => (
        <Tag color={record.typeId?.color || "#52c41a"}>
          {record.typeId?.name}
        </Tag>
      ),
    },
    {
      title: "Lokacija",
      dataIndex: ["locationId", "name"],
      key: "location",
    },
    {
      title: "Iznos",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => (
        <span style={{ color: "#52c41a", fontWeight: 600 }}>
          +{amount.toLocaleString("sr-RS")} RSD
        </span>
      ),
      sorter: (a: ExtraIncomeEntry, b: ExtraIncomeEntry) => a.amount - b.amount,
    },
    {
      title: "Opis",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Akcije",
      key: "actions",
      width: 120,
      render: (_: any, record: ExtraIncomeEntry) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: "#1890ff" }}
          />
          <Popconfirm
            title="Da li ste sigurni?"
            description="Ova akcija će obrisati dodatni prihod."
            onConfirm={() => handleDelete(record._id)}
            okText="Da"
            cancelText="Ne"
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const totalAmount = entries.reduce((sum, entry) => sum + entry.amount, 0);

  return (
    <>
      <Modal
        title="Upravljanje dodatnim prihodima"
        open={visible}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            Zatvori
          </Button>,
        ]}
        width={1000}
        className="standardHeader standardFooter"
      >
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              Ukupno: {totalAmount.toLocaleString("sr-RS")} RSD
            </span>
            <span style={{ marginLeft: 16, color: "#8c8c8c" }}>
              ({entries.length} {entries.length === 1 ? "unos" : "unosa"})
            </span>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAddModalOpen(true)}
          >
            Dodaj novi
          </Button>
        </div>

        <Spin spinning={loading}>
          <Table
            dataSource={entries}
            columns={columns}
            rowKey="_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Ukupno ${total} unosa`,
            }}
            locale={{
              emptyText: "Nema dodatnih prihoda za ovaj period",
            }}
          />
        </Spin>
      </Modal>

      <AddExtraIncomeModal
        visible={isAddModalOpen}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
        selectedDate={dateRange[0] || dayjs()}
        locationId={locationId}
        editingEntry={editingEntry}
      />
    </>
  );
};

export default ManageExtraIncomeModal;
